<template lang="pug">
div(:class="{ 'has-data': services.length > 0 }")
  //- Mobile card list view
  template(v-if="isMobile")
    //- Loading skeleton
    template(v-if="loading")
      v-card.rounded-lg.mb-3(v-for="n in 3" :key="'skeleton-' + n" flat outlined)
        .pa-4
          v-skeleton-loader(type="list-item-three-line")

    //- Empty state
    .text-center.py-8(v-else-if="!services.length")
      .yz-regular-body-sm.neutral500--text No items found

    //- Card list
    template(v-else)
      v-card.rounded-lg.mb-3(v-for="(item, index) in services" :key="item.id" flat outlined @click="onEditItem(item)")
        .pa-4
          //- Header: Name + HQ badge + 3-dot menu
          .d-flex.align-center.justify-space-between
            .d-flex.align-center.justify-space-between.flex-grow-1(style="min-width: 0;")
              .yz-medium-body-sm.neutral700--text.text-truncate {{ item.name }}
              yz-badge(x-small color="blue-100" text-color="blue-500" border) HQ
            v-menu(offset-y left rounded="lg")
              template(#activator="{ on }")
                yz-btn(icon-only icon="$yzi-dots-horizontal" icon-size="20" size="sm" v-on="on" @click.native.stop)
              yz-list.py-0
                yz-list-item(@click="onEditItem(item)")
                  div Edit service
                yz-divider
                yz-list-item(@click="onServiceHistory(item)")
                  div Service history
                yz-divider
                yz-list-item(@click="onChangeStatus(item)")
                  div(:class="item.status === 'published' ? 'danger500--text' : 'success500--text'") {{ item.status === 'published' ? 'Archive' : 'Activate' }}
                yz-divider
                yz-list-item(@click="onDeleteItem(item)")
                  div.danger500--text Delete service

          //- Type
          .d-flex.align-center.mt-3(v-if="item.type")
            .yz-regular-body-xs.neutral500--text.mr-2 Type
            .yz-regular-body-xs.neutral700--text.text-capitalize {{ item.type }}

          //- Description
          .d-flex.align-center.mt-2(v-if="item.product && item.product.description")
            .yz-regular-body-xs.neutral500--text.mr-2 Description
            .yz-regular-body-xs.neutral700--text.text-truncate {{ item.product.description }}

          //- Cost
          .d-flex.align-center.mt-2(v-if="item.cost !== null && item.cost !== undefined")
            .yz-regular-body-xs.neutral500--text.mr-2 Cost
            .yz-regular-body-xs.success500--text {{ item.cost | displayPrice($currency) }}

          //- Price to patient
          .d-flex.align-center.mt-2(v-if="item.price_range && item.price_range.length")
            .yz-regular-body-xs.neutral500--text.mr-2 Price
            .yz-regular-body-xs.success500--text {{ item.price_range[0] | displayPrice($currency) }}
              span(v-if="item.price_range.length > 1") &nbsp;- {{ item.price_range[1] | displayPrice($currency) }}

          //- Branches
          .d-flex.align-center.mt-2(v-if="item.assigned_branches && item.assigned_branches.length")
            .yz-regular-body-xs.neutral500--text.mr-2 Branches
            yz-truncated-list(:items="item.assigned_branches" text-item="name" :gap="4")

  //- Desktop table view
  .table-scroll-wrapper(v-else)
    yz-data-table(:headers="tableHeaders" :items="services" item-key="id" :loading="loading" :server-items-length="totalCount" no-outer-border radius="0" disable-sort body-height="52px" header-height="36px" header-class="yz-bold-body-xs" hide-default-footer :items-per-page="pageSize" @click:row="onEditItem")
      //- Name
      template(v-slot:item.name="{ item }")
        .d-flex.align-center.justify-space-between
          .yz-medium-body-sm.neutral700--text.mr-2 {{ item.name }}
          yz-badge(x-small color="blue-100" text-color="blue-500" border) HQ
      //- Type
      template(v-slot:item.type="{ item }")
        .yz-regular-body-sm.neutral700--text.text-capitalize {{ item.type }}
      //- Description
      template(v-slot:item.description="{ item }")
        yz-tooltip(v-if="item.product && item.product.description")
          template(#tooltip)
            div {{ item.product.description }}
          .yz-regular-body-sm.neutral700--text.text-truncate(style="max-width: 200px;") {{ item.product.description }}
      //- Cost
      template(v-slot:item.cost="{ item }")
        .yz-regular-body-sm.success500--text(v-if="item.cost !== null && item.cost !== undefined") {{ item.cost | displayPrice($currency) }}
      //- Price to patient
      template(v-slot:item.priceToPatient="{ item }")
        .yz-regular-body-sm.success500--text(v-if="item.price_range && item.price_range.length") {{ item.price_range[0] | displayPrice($currency) }}
          span(v-if="item.price_range.length > 1") &nbsp;- {{ item.price_range[1] | displayPrice($currency) }}
      //- Branches
      template(v-slot:item.branches="{ item }")
        yz-truncated-list(v-if="item.assigned_branches && item.assigned_branches.length" :items="item.assigned_branches" text-item="name" :gap="4")
      //- Status
      template(v-slot:item.status="{ item }")
        yz-badge(size="md" :color="item.status === 'published' ? 'green-100' : 'red-100'" :text-color="item.status === 'published' ? 'success-500' : 'danger-500'")
          .status-dot.mr-1(:class="item.status === 'published' ? 'active' : 'archived'")
          span {{ item.status === 'published' ? 'Active' : 'Archived' }}
      //- Actions
      template(v-slot:item.actions="{ item, index }")
        .d-flex.align-center.justify-end(@click.stop)
          v-menu(offset-y :top="menuPositionTop(index)" left rounded="lg")
            template(#activator="{ on }")
              yz-btn(icon-only icon="$yzi-dots-horizontal" icon-size="20" size="lg" v-on="on")
            yz-list.py-0
              yz-list-item(@click="onEditItem(item)")
                div Edit service
              yz-divider
              yz-list-item(@click="onServiceHistory(item)")
                div Service history
              yz-divider
              yz-list-item(@click="onChangeStatus(item)")
                div(:class="item.status === 'published' ? 'neutral700--text' : 'success500--text'") {{ item.status === 'published' ? 'Archive' : 'Activate' }}
              yz-divider
              yz-list-item(@click="onDeleteItem(item)")
                div.danger500--text Delete service

  //- Pagination
  .d-flex.align-center.mt-4(v-if="totalCount > pageSize")
    v-spacer
    yz-pagination(v-model="currentPage" version="yc" :page-size.sync="pageSize" :count="totalCount" @input="onPageChanged")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    isMobile: { type: Boolean, default: false },
    activeTab: { type: Number, default: 0 },
    searchText: { type: String, default: '' },
    filters: { type: Array, default: () => [] },
    locationFilter: { default: null }
  },
  data: () => ({
    loading: false,
    services: [],
    totalCount: 0,
    currentPage: 1,
    pageSize: 20
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      hqServicesFields: 'hq/getServicesFields'
    }),
    tableHeaders () {
      const name = { text: 'NAME', value: 'name', sortable: false }
      const fields = this.hqServicesFields.filter(item => item.enable).map(item => ({
        text: item.field_name.toUpperCase(),
        value: item.id,
        sortable: false,
        ...(item.width ? { width: item.width } : {})
      }))
      const actions = { text: '', value: 'actions', sortable: false, width: 80 }
      return [name, ...fields, actions]
    }
  },
  watch: {
    activeTab () {
      this.currentPage = 1
      this.fetchServices()
    },
    filters () {
      this.currentPage = 1
      this.fetchServices()
    },
    locationFilter () {
      this.currentPage = 1
      this.fetchServices()
    }
  },
  mounted () {
    this.fetchServices()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchServices () {
      this.loading = true
      const params = {
        page: this.currentPage,
        page_size: this.pageSize
      }
      // Tab filters
      if (this.activeTab === 1) params.type = 'service'
      if (this.activeTab === 2) params.type = 'procedure'
      if (this.activeTab === 3) params.type = 'investigation'
      if (this.activeTab === 4) params.status = 'draft'
      // Search
      if (this.searchText) params.search = this.searchText
      // Filters
      this.filters.forEach(filter => {
        params[filter.key] = filter.value
      })
      // Location filter
      if (this.locationFilter) params.branches = this.locationFilter.join(',')

      this.$axios.get(`/clinics/${this.company.slug}/hq/services`, { params })
        .then(resp => {
          this.services = resp.data.results || []
          this.totalCount = resp.data.count || 0
        })
        .catch(err => {
          const message = err.response?.data?.detail
          if (message && message.includes('Invalid page')) {
            this.currentPage = 1
            this.fetchServices()
          } else {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    onPageChanged () {
      this.fetchServices()
    },
    onEditItem (item) {
      this.$emit('edit-item', item)
    },
    onServiceHistory (item) {
      this.$router.push({ name: 'HQServiceHistory', params: { id: item.id } })
    },
    onChangeStatus (item) {
      const newStatus = item.status === 'published' ? 'draft' : 'published'
      this.$axios.patch(`/clinics/${this.company.slug}/hq/services/${item.id}`, { product: { status: newStatus } })
        .then(() => {
          const label = newStatus === 'draft' ? 'archived' : 'activated'
          this.showMessage(['success', `Service ${label} successfully`])
          this.fetchServices()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    onDeleteItem (item) {
      this.$emit('delete-item', item)
    },
    menuPositionTop (index) {
      const position = index + 1
      if (position > 7) {
        const start = this.services.length - 3
        if (position > start) return true
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
.has-data ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover > td {
  cursor: pointer;
  background: var(--yz-neutral-200) !important;
}
.table-scroll-wrapper {
  overflow-x: auto;
}
.table-scroll-wrapper ::v-deep .yz-data-table {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper > table {
  min-width: 1400px;
}
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  &.active { background-color: var(--yz-success-500); }
  &.archived { background-color: var(--yz-danger-500); }
}
</style>
